import React, { FC } from "react";

export interface TaggableUser {
    userid: number;
    displayName: string;
    internal: boolean;
}

const STORED_MENTION = /@\[([^\]\n]{1,120})\]\((\d{1,10})\)/g;

export type MentionChunk =
    | { type: "text"; value: string }
    | { type: "mention"; value: string; userid: number };

export const splitMentions = (text: string): MentionChunk[] => {
    if (!text) return [];
    const chunks: MentionChunk[] = [];
    let last = 0;
    STORED_MENTION.lastIndex = 0;
    let match = STORED_MENTION.exec(text);
    while (match !== null) {
        if (match.index > last) {
            chunks.push({ type: "text", value: text.slice(last, match.index) });
        }
        chunks.push({ type: "mention", value: match[1], userid: Number(match[2]) });
        last = match.index + match[0].length;
        match = STORED_MENTION.exec(text);
    }
    STORED_MENTION.lastIndex = 0;
    if (last < text.length) chunks.push({ type: "text", value: text.slice(last) });
    return chunks;
};

export const toDraft = (text: string): string =>
    splitMentions(text)
        .map((chunk) => (chunk.type === "mention" ? `@${chunk.value}` : chunk.value))
        .join("");

export const existingMentions = (text: string): TaggableUser[] => {
    const byId = new Map<number, TaggableUser>();
    splitMentions(text).forEach((chunk) => {
        if (chunk.type === "mention" && !byId.has(chunk.userid)) {
            byId.set(chunk.userid, {
                userid: chunk.userid,
                displayName: chunk.value,
                internal: true,
            });
        }
    });
    return [...byId.values()];
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const serializeMentions = (draft: string, picked: TaggableUser[]): string => {
    if (!draft || picked.length === 0) return draft;
    const byName = new Map<string, TaggableUser[]>();
    picked.forEach((user) => {
        const sameName = byName.get(user.displayName) || [];
        if (!sameName.some((p) => p.userid === user.userid)) sameName.push(user);
        byName.set(user.displayName, sameName);
    });
    const names = [...byName.keys()].sort((a, b) => b.length - a.length);
    return names.reduce((text, name) => {
        const users = byName.get(name) || [];
        let seen = 0;
        return text.replace(
            new RegExp(`@${escapeRegExp(name)}(?![\\w'-])`, "g"),
            (match) => {
                const user = users.length === 1 ? users[0] : users[seen];
                seen += 1;
                return user ? `@[${name}](${user.userid})` : match;
            },
        );
    }, draft);
};

export const activeMentionQuery = (
    value: string,
    caret: number,
): { query: string; start: number } | null => {
    const upToCaret = value.slice(0, caret);
    const at = upToCaret.lastIndexOf("@");
    if (at === -1) return null;
    const before = at > 0 ? upToCaret[at - 1] : "";
    if (before && !/[\s(]/.test(before)) return null;
    const query = upToCaret.slice(at + 1);
    if (query.length > 60 || /[\n]/.test(query)) return null;
    return { query, start: at };
};

export const applyMention = (
    value: string,
    caret: number,
    start: number,
    user: TaggableUser,
): { value: string; caret: number } => {
    const rest = value.slice(caret);
    const inserted = `@${user.displayName}${/^\s/.test(rest) ? "" : " "}`;
    return {
        value: value.slice(0, start) + inserted + rest,
        caret: start + inserted.length,
    };
};

export const filterTaggable = (users: TaggableUser[], query: string): TaggableUser[] => {
    const needle = query.trim().toLowerCase();
    const matches = needle
        ? users.filter((user) => user.displayName.toLowerCase().includes(needle))
        : users;
    return matches.slice(0, 8);
};

export const MentionText: FC<{ text: string }> = ({ text }) => (
    <>
        {splitMentions(text).map((chunk, index) =>
            chunk.type === "mention" ? (
                <span
                    key={index}
                    className="text-accent-text bg-tint rounded px-1 py-px font-semibold"
                >
                    @{chunk.value}
                </span>
            ) : (
                <React.Fragment key={index}>{chunk.value}</React.Fragment>
            ),
        )}
    </>
);
